import {DocumentNode, useLazyQuery} from '@apollo/client';
import {ApolloError} from '@apollo/client/errors';
import {useCallback, useEffect, useState} from 'react';
import {BehaviorSubject, Observable} from 'rxjs';

type LazyQuery$<QueryInput, QueryResult> = {
    lastRun$: Observable<Date | null>;
    isRunning$: Observable<boolean>;
    execute: (inputOverrides?: Partial<QueryInput>) => Promise<void>;
    data: QueryResult | undefined;
    variables: QueryInput | undefined;
    error: ApolloError | undefined;
};

export const useLazyQuery$ = <QueryInput, QueryResult>(
    query: DocumentNode,
    queryVariables: QueryInput,
): LazyQuery$<QueryInput, QueryResult> => {
    const [lastRun$] = useState(new BehaviorSubject<Date | null>(null));
    const [isRunning$] = useState(new BehaviorSubject<boolean>(false));

    const [doRun, {called, data, variables, loading, error, refetch}] = useLazyQuery<QueryResult, QueryInput>(
        query, {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-and-network',
            variables: queryVariables,
            onCompleted: () => lastRun$.next(new Date()),
        });
    useEffect(() => {
        if (loading !== isRunning$.getValue()) {
            isRunning$.next(loading);
        }
    }, [loading, isRunning$]);


    const execute = useCallback(async (inputOverrides?: Partial<QueryInput>) => {
        if (called && !!refetch) {
            await refetch(inputOverrides);
        } else {
            doRun();
        }
    }, [doRun, refetch, called]);


    return {
        lastRun$: lastRun$ as Observable<Date | null>,
        isRunning$: isRunning$ as Observable<boolean>,
        execute,
        data,
        variables,
        error,
    }
};
